  /**
   * Near Miss Report Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<NearMissReport> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (employeeJobDetails, incidentBasics, equipment, incidentAssessment, signOff)
   * - Implements checkbox group validation requiring at least 1 near miss type selection
   * - Provides comprehensive incident reporting with employee and job details
   * - Implements conditional validation for multiple fields based on user selections
   * - Implements form validation for safety compliance and incident documentation
   * - Implements step validation with attachment and signature requirements
   * - Flattens nested form structure for backend submission
   * 
   * Unique Complex Logic:
   * - Dynamic attachment requirement changes (3 attachments required when property damage is "Yes")
   * - Multiple conditional validation setups for fields that become required based on other field values
   * - Checkbox group validation using custom validator requiring minimum selection
   * - Real-time validation updates for conditional fields with subscription management
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

  
import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { NearMissReport } from '../../../../../models/forms/near-miss-report/near-miss-report.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { simpleCheckboxValidator } from '../../../../../utils/form-validators';
import { environment } from '../../../../../../environments/environment';

/**
 * Component for handling Near Miss Report submissions.
 * This form is used to document near miss incidents,
 * ensuring proper safety reporting and analysis.
 */
@Component({
  selector: 'app-near-miss-report',
  templateUrl: './near-miss-report.component.html',
  styleUrls: ['./near-miss-report.component.css'],
})
export class NearMissReportComponent
  extends BaseFormComponent<NearMissReport>
  implements OnInit
{
  // Form type identifier used for routing and form metadata lookup
  override formType = NearMissReport.getFormInfo().formType;

  // Instance of the form data model
  override model = new NearMissReport();
  
  // Define the form steps and their properties
  override steps: FormStep[] = NearMissReport.getFormSteps();

  typeOfNearMissCheckboxes: { name: string; label: string }[] = [];





  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );

    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  // Static methods to get the checkbox groupings for the component
  getTypeOfNearMissCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'injuryNearMiss', label: this.fieldMappings['injuryNearMiss'] },
      { name: 'propertyDamage', label: this.fieldMappings['propertyDamage'] },
      { name: 'otherNearMiss', label: this.fieldMappings['otherNearMiss'] },
    ];
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.typeOfNearMissCheckboxes = this.getTypeOfNearMissCheckboxes();
    const typeOfNearMissGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 near miss type
      }
    );// Create form groups for checkboxes with validators
    
    this.typeOfNearMissCheckboxes.forEach((checkbox) => {
      typeOfNearMissGroup.addControl(checkbox.name, this.fb.control(false));
    });

    this.form = this.fb.group({
      employeeJobDetails: this.fb.group({
        // Employee Information
        employeeFirstName: [this.model.employeeFirstName, Validators.required],
        employeeLastName: [this.model.employeeLastName, Validators.required],
        dateOfBirth: [this.model.dateOfBirth, Validators.required],
        dateOfHire: [this.model.dateOfHire, Validators.required],
        craftAndPosition: [this.model.craftAndPosition, Validators.required],
        
        // Job Information
        jobName: [this.model.jobName, Validators.required],
        shiftWorked: [this.model.shiftWorked, Validators.required],
        supervisorFirstName: [
          this.model.supervisorFirstName,
          Validators.required,
        ],
        supervisorLastName: [
          this.model.supervisorLastName,
          Validators.required,
        ],
        hoursWorkedPerDay: [this.model.hoursWorkedPerDay, Validators.required],
      }),
      incidentBasics: this.fb.group({
        // Type of Near Miss
        typeOfNearMiss: typeOfNearMissGroup,
        specifyOther: [this.model.specifyOther],

        // Incident Timing & Location
        dateOfIncident: [this.model.dateOfIncident, Validators.required],
        timeOfIncident: [this.model.timeOfIncident, Validators.required],
        dateReported: [this.model.dateReported, Validators.required],
        timeReported: [this.model.timeReported, Validators.required],
        incidentDescription: [
          this.model.incidentDescription,
          Validators.required,
        ],
        incidentReportedToFirstName: [
          this.model.incidentReportedToFirstName,
          Validators.required,
        ],
        incidentReportedToLastName: [
          this.model.incidentReportedToLastName,
          Validators.required,
        ],
        weather: [this.model.weather, Validators.required],
        incidentLocation: [this.model.incidentLocation, Validators.required],
        
        // Witnesses
        witness1: [this.model.witness1, Validators.required],
        witness2: [this.model.witness2],
      }),
      equipment: this.fb.group({
        // Equipment & Safety
        equipmentTool1: [this.model.equipmentTool1, Validators.required],
        equipmentTool2: [this.model.equipmentTool2],
        anyDefects: [this.model.anyDefects],
        coveredInPreTask: [this.model.coveredInPreTask, Validators.required],
        
        // PPE
        wasPpeRequired: [this.model.wasPpeRequired, Validators.required],
        wasPpeAvailable: [this.model.wasPpeAvailable, Validators.required],
      }),
     
      incidentAssessment: this.fb.group({
        // Injury/Damage Assessment
        othersInvolved: [this.model.othersInvolved, Validators.required],
        othersExplanation: [this.model.othersExplanation, Validators.required],
        injured: [this.model.injured, Validators.required],
        couldHaveBeenInjured: [
          this.model.couldHaveBeenInjured,
,
        ],
        bloodExposure: [this.model.bloodExposure, Validators.required],
        seekMedicalTreatment: [
          this.model.seekMedicalTreatment,
          Validators.required,
        ],
        previousExposure: [this.model.previousExposure, Validators.required],
        aggravationOfPreviousInjury: [
          this.model.aggravationOfPreviousInjury,
          Validators.required,
        ],
        describePreviousInjury: [
          this.model.describePreviousInjury,
          Validators.required,
        ],
        hasPropertyDamage: [this.model.hasPropertyDamage, Validators.required],
        damageDescription: [this.model.damageDescription],
        worstCaseScenario: [this.model.worstCaseScenario, Validators.required],

              
        preventionSuggestion: [
          this.model.preventionSuggestion,
          Validators.required,
        ],
      }),
      signOff: this.fb.group({
        signatureDate: [this.model.signatureDate],
      }),
    });

    this.setupConditionalValidators();
    

  }

  // Form group getters
  get employeeJobDetailsFormGroup(): FormGroup {
    return this.form.get('employeeJobDetails') as FormGroup;
  }

  get incidentBasicsFormGroup(): FormGroup {
    return this.form.get('incidentBasics') as FormGroup;
  }

  get typeOfNearMissCheckboxesFormGroup(): FormGroup {
    return this.incidentBasicsFormGroup.get('typeOfNearMiss') as FormGroup;
  }

  get equipmentFormGroup(): FormGroup {
    return this.form.get('equipment') as FormGroup;
  }

  get incidentAssessmentFormGroup(): FormGroup {
    return this.form.get('incidentAssessment') as FormGroup;
  }

  get signOffFormGroup(): FormGroup {
    return this.form.get('signOff') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new NearMissReport instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): NearMissReport {
    const formValue = this.form.value;

    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Employee Information
      ...formValue.employeeJobDetails,

      // Incident Basics (including checkbox group)
      ...formValue.incidentBasics.typeOfNearMiss,
      specifyOther: formValue.incidentBasics.specifyOther,
      dateOfIncident: formValue.incidentBasics.dateOfIncident,
      timeOfIncident: formValue.incidentBasics.timeOfIncident,
      dateReported: formValue.incidentBasics.dateReported,
      timeReported: formValue.incidentBasics.timeReported,
      incidentReportedToFirstName: formValue.incidentBasics.incidentReportedToFirstName,
      incidentReportedToLastName: formValue.incidentBasics.incidentReportedToLastName,
      weather: formValue.incidentBasics.weather,
      incidentLocation: formValue.incidentBasics.incidentLocation,
      incidentDescription: formValue.incidentBasics.incidentDescription,
      witness1: formValue.incidentBasics.witness1,
      witness2: formValue.incidentBasics.witness2,

      // Equipment
      equipmentTool1: formValue.equipment.equipmentTool1,
      equipmentTool2: formValue.equipment.equipmentTool2,
      anyDefects: formValue.equipment.anyDefects,
      coveredInPreTask: formValue.equipment.coveredInPreTask,
      wasPpeRequired: formValue.equipment.wasPpeRequired,
      wasPpeAvailable: formValue.equipment.wasPpeAvailable,

      // Incident Assessment
      ...formValue.incidentAssessment,

      // Sign Off
      ...formValue.signOff,
    };

    const nearMissReport = new NearMissReport(flattenedData);
    const updatedNearMissReport = this.updateModelWithAttachments(nearMissReport);
    return updatedNearMissReport;


  }

  /**
   * Sets up validation rules for each step of the near miss report form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Basic Info: Requires employee information and incident type
   * - Incident Details: Requires incident timing, location, and circumstances
   * - Analysis & Assessment: Requires detailed incident analysis and prevention measures
   * - Attachments & Signature: Requires attachment uploads and signature
   *
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required attachments are uploaded
   * 3. Required signatures are collected
   * 4. Form data meets safety and compliance requirements
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
  

    // Define step indices for attachments and signatures
    const UPLOAD_REQUIREMENT_INDICES: number = 0; // Upload requirement at index 0
    const SIGNATURE_REQUIREMENT_INDEX: number = 0; // Signature requirement at index 0

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('employee-job-details', this.employeeJobDetailsFormGroup);
    this.setupStepValidation(
      'incident-basics',
      this.incidentBasicsFormGroup
    );
    this.setupStepValidation(
      'equipment',
      this.equipmentFormGroup
    );
    this.setupStepValidation(
      'incident-assessment',
      this.incidentAssessmentFormGroup, 
      {
        attachmentIndex: [UPLOAD_REQUIREMENT_INDICES],
      }
    );
    this.setupStepValidation(
      'sign-off',
      this.signOffFormGroup,
      {
        attachmentIndex: [UPLOAD_REQUIREMENT_INDICES],
        signatureIndex: [SIGNATURE_REQUIREMENT_INDEX],
      }
    );
  }

  /**
   * Sets up conditional validation for the "Other Near Miss" field.
   * When "Other Near Miss" is selected, the "Specify Other" field becomes required.
   */
  private setupConditionalValidators(): void {
    // Skip conditional validators if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }

    this.setupOtherNearMissValidator();
    this.setupOthersInvolvedExplanationValidator();
    this.setupCouldHaveBeenInjuredValidator();
    this.setupPreviousInjuryValidator();
    this.setupDamageDescriptionValidator();
  }


  private setupOtherNearMissValidator(): void {
    // Type of Near Miss
    this.typeOfNearMissCheckboxesFormGroup
      .get('otherNearMiss')
      ?.valueChanges.subscribe((checked) => {
        const control = this.incidentBasicsFormGroup.get('specifyOther');
        if (checked) {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  //Other's involed explanation
  private setupOthersInvolvedExplanationValidator(): void {
    this.incidentAssessmentFormGroup
      .get('othersInvolved')
      ?.valueChanges.subscribe((value) => {
        const control = this.incidentAssessmentFormGroup.get('othersExplanation');
        if (value === 'Yes') {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }


  //Could have been injured
  private setupCouldHaveBeenInjuredValidator(): void {
    this.incidentAssessmentFormGroup
      .get('injured')
      ?.valueChanges.subscribe((value) => {
        const control = this.incidentAssessmentFormGroup.get('couldHaveBeenInjured');
        if (value === 'No') {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  //Previous Injury
  private setupPreviousInjuryValidator(): void {
    this.incidentAssessmentFormGroup
      .get('aggravationOfPreviousInjury')
      ?.valueChanges.subscribe((value) => {
        const control = this.incidentAssessmentFormGroup.get('describePreviousInjury');
        if (value === 'Yes') {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  // When property damage is yes, the damage description and attachment upload are required
  // The attachment upload is required to be 3 attachments 
  private setupDamageDescriptionValidator(): void {
    this.incidentAssessmentFormGroup
      .get('hasPropertyDamage')
      ?.valueChanges.subscribe((value) => {
        const control = this.incidentAssessmentFormGroup.get('damageDescription');
        if (value === 'Yes') {
          control?.setValidators([Validators.required]);
          this.formAttachmentRequirements!.uploads![0].required = 3;
          this.attachmentService.setAttachmentRequirements(this.formAttachmentRequirements);;
        } else {
          control?.clearValidators();
          this.formAttachmentRequirements!.uploads![0].required = 0;
        }
        control?.updateValueAndValidity();
      });

  }

  
 
}
